<div class="attendance-report-container">
  <mat-card>
    <mat-card-header>
      <mat-card-title>
        <mat-icon>assessment</mat-icon>
        Attendance Report
      </mat-card-title>
      <mat-card-subtitle>View employee attendance records by date range</mat-card-subtitle>
    </mat-card-header>

    <mat-card-content>
      <!-- Filter Form -->
      <div class="filters-section">
        <div class="filters-header">
          <div class="icon">
            <mat-icon>filter_list</mat-icon>
          </div>
          <h3>Filter Options</h3>
        </div>
        <form [formGroup]="filterForm" class="filters-form">
          <div class="form-field">
            <mat-form-field appearance="outline">
              <mat-label>Employee</mat-label>
              <mat-select formControlName="employeeId">
                <mat-option value="">All Employees</mat-option>
                <mat-option *ngFor="let employee of employees" [value]="employee.id">
                  {{ employee.fullName }}
                </mat-option>
              </mat-select>
              <mat-icon matSuffix>person</mat-icon>
            </mat-form-field>
          </div>

          <div class="form-field">
            <mat-form-field appearance="outline">
              <mat-label>Start Date</mat-label>
              <input matInput [matDatepicker]="startPicker" formControlName="startDate">
              <mat-datepicker-toggle matSuffix [for]="startPicker"></mat-datepicker-toggle>
              <mat-datepicker #startPicker></mat-datepicker>
            </mat-form-field>
          </div>

          <div class="form-field">
            <mat-form-field appearance="outline">
              <mat-label>End Date</mat-label>
              <input matInput [matDatepicker]="endPicker" formControlName="endDate">
              <mat-datepicker-toggle matSuffix [for]="endPicker"></mat-datepicker-toggle>
              <mat-datepicker #endPicker></mat-datepicker>
            </mat-form-field>
          </div>

          <div class="filter-actions">
            <button mat-raised-button color="primary" (click)="generateReport()" [disabled]="loading" class="filter-btn">
              <mat-icon>search</mat-icon>
              Generate Report
            </button>
          </div>
        </form>
      </div>

      <!-- Summary Cards -->
      <div class="summary-cards" *ngIf="dataSource.data.length > 0">
        <div class="summary-card">
          <div class="card-header">
            <div class="icon total-hours">
              <mat-icon>schedule</mat-icon>
            </div>
            <div class="title">Total Hours</div>
          </div>
          <div class="value">{{ getTotalHours() | number:'1.2-2' }}</div>
        </div>

        <div class="summary-card">
          <div class="card-header">
            <div class="icon total-days">
              <mat-icon>calendar_today</mat-icon>
            </div>
            <div class="title">Total Days</div>
          </div>
          <div class="value">{{ getTotalDays() }}</div>
        </div>

        <div class="summary-card">
          <div class="card-header">
            <div class="icon total-employees">
              <mat-icon>people</mat-icon>
            </div>
            <div class="title">Employees</div>
          </div>
          <div class="value">{{ getTotalEmployees() }}</div>
        </div>

        <div class="summary-card">
          <div class="card-header">
            <div class="icon face-issues">
              <mat-icon>warning</mat-icon>
            </div>
            <div class="title">Face Issues</div>
          </div>
          <div class="value">{{ getEmployeesWithFaceIssues() }}</div>
        </div>
      </div>

      <!-- Search Filter -->
      <div class="data-grid" *ngIf="dataSource.data.length > 0">
        <div class="grid-header">
          <div class="search-field">
            <mat-form-field appearance="outline">
              <mat-label>Search Records</mat-label>
              <input matInput (keyup)="applyFilter($event)" placeholder="Search by employee name" #input>
              <mat-icon matSuffix>search</mat-icon>
            </mat-form-field>
          </div>

          <div class="grid-actions">
            <button mat-raised-button color="accent" (click)="exportToCSV()" [disabled]="dataSource.data.length === 0" class="export-btn">
              <mat-icon>download</mat-icon>
              Export CSV
            </button>
            <button mat-raised-button color="primary" routerLink="../import" class="import-btn">
              <mat-icon>upload_file</mat-icon>
              Import Attendance
            </button>
          </div>
        </div>
      </div>

      <!-- Attendance Table -->
      <div class="table-container mat-elevation-z8" *ngIf="dataSource.data.length > 0">
        <table mat-table [dataSource]="dataSource" multiTemplateDataRows class="mat-elevation-z8">
          <!-- Employee Photo Column -->
          <ng-container matColumnDef="employeePhoto">
            <th mat-header-cell *matHeaderCellDef>Photo</th>
            <td mat-cell *matCellDef="let employee">
              <div class="employee-photo-cell">
                <img *ngIf="employee.employeePhoto" 
                     [src]="employee.employeePhoto" 
                     alt="Employee Photo" 
                     class="employee-table-photo">
                <div *ngIf="!employee.employeePhoto" class="no-photo">
                  <mat-icon>person</mat-icon>
                </div>
              </div>
            </td>
          </ng-container>

          <!-- Employee Name Column -->
          <ng-container matColumnDef="employeeName">
            <th mat-header-cell *matHeaderCellDef>Employee</th>
            <td mat-cell *matCellDef="let employee">
              <div class="employee-name-cell">
                <span class="employee-name">{{ employee.employeeName }}</span>
                <mat-icon *ngIf="employee.hasFaceVerificationIssues" 
                         class="face-verification-warning" 
                         matTooltip="Face verification issues detected">
                  warning
                </mat-icon>
              </div>
            </td>
          </ng-container>

          <!-- Total Days Worked Column -->
          <ng-container matColumnDef="totalDaysWorked">
            <th mat-header-cell *matHeaderCellDef>Days Worked</th>
            <td mat-cell *matCellDef="let employee">{{ employee.totalDaysWorked }}</td>
          </ng-container>

          <!-- Total Hours Worked Column -->
          <ng-container matColumnDef="totalHoursWorked">
            <th mat-header-cell *matHeaderCellDef>Total Hours</th>
            <td mat-cell *matCellDef="let employee">{{ employee.totalHoursWorked | number:'1.2-2' }}</td>
          </ng-container>

          <!-- Expand Column -->
          <ng-container matColumnDef="expand">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let employee">
              <button mat-icon-button (click)="toggleEmployee(employee); $event.stopPropagation();">
                <mat-icon>{{ expandedEmployee === employee ? 'expand_less' : 'expand_more' }}</mat-icon>
              </button>
            </td>
          </ng-container>

          <!-- Expanded Detail Column -->
          <ng-container matColumnDef="expandedDetail">
            <td mat-cell *matCellDef="let employee" [attr.colspan]="displayedColumns.length">
              <div *ngIf="expandedEmployee === employee" class="expanded-content">
                <div class="daily-records-header">
                  <h4>Daily Records for {{ employee.employeeName }}</h4>
                </div>
                <table class="daily-records-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Total Hours</th>
                      <th>Status</th>
                      <th>Sessions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let dailyRecord of employee.dailyRecords" 
                        [ngClass]="{ 'incomplete-day': !dailyRecord.isComplete }">
                      <td>{{ dailyRecord.date | date:'dd/MM/yyyy' }}</td>
                      <td>{{ dailyRecord.totalHours | number:'1.2-2' }}</td>
                      <td>
                        <span class="status-badge" [ngClass]="{ 'complete': dailyRecord.isComplete, 'incomplete': !dailyRecord.isComplete }">
                          {{ dailyRecord.isComplete ? 'Complete' : 'Incomplete' }}
                        </span>
                      </td>
                      <td>
                        <div class="sessions-container">
                          <div *ngFor="let session of dailyRecord.sessions; let i = index" 
                               class="session-item"
                               [ngClass]="{ 'incomplete-session': isSessionIncomplete(session) }">
                            <div class="session-header">
                              <span class="session-number">Session {{ i + 1 }}</span>
                              <mat-icon *ngIf="session.faceMatchFlagged" 
                                       class="face-verification-failed" 
                                       matTooltip="Face verification failed">
                                warning
                              </mat-icon>
                            </div>
                            <div class="session-details">
                              <div class="session-time">
                                <span class="time-label">In:</span> 
                                <span class="time-value">{{ session.clockInTime | date:'shortTime' }}</span>
                              </div>
                              <div class="session-time">
                                <span class="time-label">Out:</span> 
                                <span class="time-value">{{ session.clockOutTime | date:'shortTime' || 'Not clocked out' }}</span>
                              </div>
                              <div class="session-hours">
                                <span class="hours-label">Hours:</span> 
                                <span class="hours-value">{{ session.durationHours | number:'1.2-2' }}</span>
                              </div>
                            </div>
                            <div class="session-photo" *ngIf="session.photo">
                              <img [src]="session.photo" alt="Session Photo" class="session-photo-thumb">
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </td>
          </ng-container>

          <!-- Main Row -->
          <tr mat-row *matRowDef="let employee; columns: displayedColumns;"></tr>
          <!-- Expanded Row -->
          <tr mat-row *matRowDef="let employee; columns: ['expandedDetail'];" class="expanded-row" [hidden]="expandedEmployee !== employee"></tr>
        </table>

        <mat-paginator [pageSizeOptions]="[10, 25, 50, 100]" aria-label="Select page of attendance records"></mat-paginator>
      </div>

      <!-- Loading Spinner -->
      <div *ngIf="loading" class="loading-container">
        <mat-spinner></mat-spinner>
        <p>Generating attendance report...</p>
      </div>

      <!-- No Data Message -->
      <div *ngIf="!loading && dataSource.data.length === 0" class="no-data-container">
        <mat-icon>assessment</mat-icon>
        <p>No attendance records found</p>
        <p class="subtitle">Select a date range and click "Generate Report" to view attendance data</p>
      </div>
    </mat-card-content>
  </mat-card>
</div> 